// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2025-12-02 08:58:49 +0100 (Tue, 02 Dec 2025) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: o.powroznik $
// Revision    : $Rev: 158832 $
// Purpose     : a view is a class/tab node in templateconfig.xml
//               a view chooses it's layout which will distribute the parts
// =============================================================================
// ---Third-party imports
import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {Message, Loader, Modal, Header, Button, Icon} from 'semantic-ui-react'
import {Title} from 'react-head'

// ---Framework imports
import {mapItems, items, has, find, entries, get, each, isArray, isNil} from 'ldash'
import connect from 'uix/connect'
import {Actions, Sel} from 'uix/registries'
import {Parts} from 'uix/registry'
import {StandalonePart} from 'uix/parts'
import {ucFirst} from 'uix/utils'
import NotificationManager from 'uix/notify/component'
import {PortalTarget, GlobalKeyboardShortcuts, ViewLoader} from 'uix/components'
import {useEffect, useState, useActions, useSelector} from 'uix/hooks'
import log from 'uix/logging'
import {applyTheme} from 'uix/styles/variables'
import {setCustomVital} from 'uix/utils/vitals'

// ---Local imports
import {DomainStyles} from './css'
import * as Layouts from './layouts'
import {ServerChooser} from './components'

// ---Globals
export const BootLoader = ({vital = 'XBL', step = 0}) => {
  setCustomVital(vital)
  return (
    <ViewLoader active={!!step}>
      {<span style={{letterSpacing: '-2px'}}>{'.'.repeat(step)}</span>}
    </ViewLoader>
  )
}

const AppLoader = connect(
  'AppLoader',
  'debug.numActions',
  ({numActions}) => {
    let [start] = useState(numActions)
    return <BootLoader vital="XAL" step={Math.max(numActions - start, 0)} />
  }
)

let ErrorMessage = ({header, errors}) => (
  <Message negative>
    <Message.Header>{header}</Message.Header>
    {errors.map((e, i) => (
      <p key={i}>{e}</p>
    ))}
  </Message>
)

class _InitAppError extends Component {
  static storeMap = ['debug.errors', 'config.deploy', 'config.apiurl']
  render() {
    let {deploy, apiurl: url, errors} = this.props
    if (deploy.mode == 'npm') {
      return <ServerChooser message={`API Init Error on: ${url}`} />
    }

    errors = errors.map(e => `${e.message} [${e.code}] on ${e.url}`)

    return (
      <>
        <Title>VL - Initialization Failure</Title>
        <ErrorMessage header="Initialization Failure" errors={errors} />
      </>
    )
  }
}
const InitAppError = connect(
  'InitAppError',
  _InitAppError
)

const ViewNotFound = connect(
  'ViewNotFound',
  'meta.router404',
  ({router404, text}) => {
    let errors = []
    if (text) {
      errors.push(text)
    }
    if (router404) {
      errors.push(`${router404.pathname} not found`)
    }
    return (
      <>
        <Title>VL - 404</Title>
        <ErrorMessage header="404" errors={errors} />
      </>
    )
  }
)

let ErrorLines = ({errors}) => {
  return errors.map((e, i) => (
    <div key={i} style={{fontSize: '1.0em'}}>
      <tt>
        {e.code}: {e.message}
      </tt>
    </div>
  ))
}

let ErrorModal = connect(
  'ErrorModal',
  ['router.pathname', 'debug.errors'],
  ({pathname, errors}) => (
    <Modal size="tiny" open={true} dimmer>
      <Header as="h3" icon={{name: 'bug', color: 'red'}} content="Fatal Error" />
      <Modal.Content>
        App terminated, restart needed
        <ErrorLines errors={errors} />
      </Modal.Content>
      <Modal.Actions>
        <Button basic color="red" as="a" href={`${pathname}`} icon="remove" content="Restart" />
      </Modal.Actions>
    </Modal>
  )
)

let OfflineModal = ({}) => {
  let [ignore, setIgnore] = useState(false)
  if (ignore) return null
  return (
    <Modal size="tiny" open={true} dimmer>
      <Header as="h3" icon={{name: 'wifi', color: 'red'}} content="Connection lost" />
      <Modal.Content style={{height: '20%'}}>
        <Loader style={{color: 'var(--vl_body_color)'}} inline indeterminate>
          Waiting for reconnect...
        </Loader>
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          color="red"
          as="a"
          icon="play"
          content="Ignore"
          onClick={() => setIgnore(true)}
        />
      </Modal.Actions>
    </Modal>
  )
}

let UnauthorizedModal = ({}) => {
  let i18n = useSelector(Sel.i18n)
  return (
    <Modal size="mini" basic open={true}>
      <Message icon>
        <Icon name="lock" />
        <Message.Content>{i18n.span('msg_insufficient_permission')}</Message.Content>
      </Message>
    </Modal>
  )
}
let LoginModal = ({classcfg, tabcfg}) => {
  /* visible if appstate VIEW_UNAUTHORIZED
   */
  let setAppState = useActions(Actions.setAppState)

  if (classcfg.noReloginDialog || tabcfg.noReloginDialog) {
    return <UnauthorizedModal />
  }
  return (
    <Modal basic open={true} dimmer="blurring">
      <StandalonePart type="AuthLogin" />
    </Modal>
  )
}

class View extends Component {
  static storeMap = root => {
    let request = root.meta.request
    return {
      // official deps
      appstate: Sel.appstate(root),
      runId: Sel.runId(root),
      dname: request.dname,
      classname: request.classname,
      tabname: request.tabname,
      classcfg: Sel.classCfg(root),
      // some hacks ahead
      pprintOn: !isNil(root.meta.pprint),
      debugFooterOn: Sel.isDebugOn(root),
      themeEditorOn: root.debug.isThemeEditorOn,
      contextHelpOn: root.meta.contextHelp.on,
      theme: root.meta.theme.name,
      logStreamOn: root.meta.logstream.on,
      logStreamMode: root.meta.logstream.mode,
      // we cant bind this here, will triggere re-render
      progressVisible: true, //get(root, 'meta.progress.visible'),
    }
  }

  constructor(props) {
    super(props)
    this.state = {
      loadingPartData: false,
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.theme !== prevProps.theme) {
      applyTheme(this.props.theme)
    }
  }

  getAutoParts(layout) {
    // XXX: should also come from tcfg
    let parts = []
    let {
      progress,
      pprintOn,
      debugFooterOn,
      progressVisible,
      themeEditorOn,
      contextHelpOn,
      logStreamOn,
      logStreamMode,
    } = this.props
    parts.push([Parts.get('HTMLHead'), {type: 'HTMLHead'}])

    if (progressVisible) {
      parts.push([Parts.get('FilterProgress'), {type: 'FilterProgress', align: 'top', order: -100}])
    }

    if (debugFooterOn) {
      parts.push([Parts.get('DebugFooter'), {type: 'DebugFooter', align: 'bottom', order: 100}])
    }

    if (layout.startsWith('L131')) {
      if (themeEditorOn) {
        parts.push([Parts.get('ThemeBuilder'), {type: 'ThemeBuilder', align: 'right'}])
      }
      if (contextHelpOn) {
        parts.push([Parts.get('ContextHelp'), {type: 'ContextHelp', align: 'right'}])
      }
      if (pprintOn) {
        parts.push([Parts.get('PPrintViz'), {type: 'PPrintViz', align: 'right'}])
      }
      if (logStreamOn) {
        parts.push([
          Parts.get('LogConsole'),
          {type: 'LogConsole', align: logStreamMode == 'dialog' ? 'dialog' : 'right'},
        ])
      }
    }
    return parts
  }

  getParts(classcfg, tabcfg, layout) {
    let classlevel = [...this.getAutoParts(layout)]
    let tablevel = []

    if (!classcfg || !tabcfg) {
      log.error('invalid view config')
    } else {
      each([[classcfg, classlevel], [tabcfg, tablevel]], ([cfg, level]) => {
        each(cfg.parts, partcfg => {
          let {type, ...pcfg} = partcfg
          // first char is uppercased - react component convention
          type = ucFirst(type)

          let Part = Parts.get(type)
          if (!Part) {
            pcfg.unknownType = type
            type = 'UnknownPart'
          }
          level.push([Parts.get(type), {type, ...pcfg}])
        })
      })
    }

    return {classlevel, tablevel}
  }

  render() {
    const {appstate, classname, tabname, dname, classcfg, runId, theme} = this.props

    if (appstate == 'INIT_APP_ERROR' || appstate == 'API_SWITCH') {
      return <InitAppError />
    }

    if (appstate == 'INIT_APP_404') {
      return <ViewNotFound />
    }

    if (runId === 0 || appstate === 'INIT_APP') {
      // waiting for application boot
      return <AppLoader />
    }

    if (!classcfg) {
      return <ViewNotFound text={`tcfg class not found: ${dname}/${classname}`} />
    }

    if (!tabname) {
      return <ViewNotFound text={'no tab selected'} />
    }

    let tabcfg = classcfg.tabs[tabname]
    if (!tabcfg) {
      return <ViewNotFound text={`tcfg tab not found: ${dname}/${classname}/${tabname}`} />
    }

    let errors = null
    if (appstate == 'FATAL_ERROR') {
      errors = <ErrorModal />
    }
    if (appstate == 'API_OFFLINE') {
      errors = <OfflineModal />
    }
    if (appstate == 'VIEW_UNAUTHORIZED') {
      errors = <LoginModal classcfg={classcfg} tabcfg={tabcfg} />
    }

    let layout = get(tabcfg, 'layout', get(classcfg, 'layout', 'L131'))

    let p = [dname, classname, tabname]
    log.info(`rendering: %o [${layout}]`, p.filter(e => e).join('/'))

    // first char is uppercased - react component convention
    let Layout = Layouts[ucFirst(layout)]
    if (!Layout) {
      return <div>NoLayout: {layout}</div>
    }

    // get part configurations
    let parts = this.getParts(classcfg, tabcfg, layout)

    return (
      <>
        {errors}
        <DomainStyles />
        <NotificationManager />
        <PortalTarget id="rootPortal" />
        <Layout parts={parts} theme={theme} />
        <GlobalKeyboardShortcuts />
      </>
    )
  }
}

const v = connect(
  'View',
  View
)
export {v as View}
